'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useSeonSession } from '@shared/hooks';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { DEPOSIT_PAYMENT_METHODS } from '../../../PaymentMethodSelector/PaymentMethods';
import type { MethodOrInstrument } from '../../../PaymentMethodSelector/types';
import type { UseDepositFlowReturn } from '../DepositSheet/hooks/useDepositFlow';
import { type RawCardData, useWorldpayCard } from '../DepositSheet/steps/CardDetailsStep/hooks/useWorldpayCard';
import type { DepositTransactionPayload } from '../DepositSheet/types';

type UseDirectDepositTransactionOptions = {
  amount: string;
  depositFlowInstance: UseDepositFlowReturn;
  onError: (message: string) => void;
  onBeforeTransaction?: () => void;
};

/**
 * Handles the logic for processing a deposit transaction for payment
 * methods that don't require a separate details step (e.g. Pay by Bank,
 * PaysafeCard, stored instruments).
 *
 * Opens a new browser tab synchronously to avoid popup blockers, validates
 * API state, optionally gathers Worldpay instrument data, then fires the
 * deposit mutation.
 */
function useDirectDepositTransaction({ amount, depositFlowInstance, onError, onBeforeTransaction }: UseDirectDepositTransactionOptions) {
  const t = useTranslations('global_sheets.deposit');
  const { session } = useSession();
  const playerId = session?.profile?.id;

  const { error: paymentsLimitsError } = useApiQuery(API_ROUTES.PAYMENTS.DEPOSIT_LIMITS, {
    credentials: true,
  });

  const { error: paymentInstrumentsError } = useApiQuery(API_ROUTES.MY_ACCOUNT.PAYMENT_INSTRUMENTS, {
    credentials: true,
    queryOptions: { retry: false },
  });

  const { getSeonSessionId } = useSeonSession();

  const { getBrowserData, getSessionId } = useWorldpayCard({
    onError: () => onError(t('aurumErrors.unknown')),
  });

  const getPaymentMethodKey = useCallback((method: MethodOrInstrument): string => {
    if (method.apiReference) return method.apiName || '';
    return Object.values(DEPOSIT_PAYMENT_METHODS).find(pm => pm.name === method.name)?.apiName || '';
  }, []);

  const processDirectDeposit = useCallback(
    async (method: MethodOrInstrument) => {
      depositFlowInstance.openNewTab();

      if (paymentsLimitsError || paymentInstrumentsError) {
        depositFlowInstance.closeNewTab();
        onError(t('aurumErrors.unknown'));
        return;
      }

      onBeforeTransaction?.();

      const idempotencyToken = `${playerId}-${Date.now()}`;
      const payload: DepositTransactionPayload = {
        idempotencyToken,
        amount: Math.round(Number(amount) * 100),
        paymentMethod: getPaymentMethodKey(method),
        acceptedTermsAndConditions: true,
      };

      if (method.apiReference) {
        try {
          const rawInstrumentData = {
            browser: await getBrowserData(),
            sessionId: await getSessionId({ cardNumber: method.number || '' } as RawCardData, idempotencyToken),
          };
          payload.instrumentData = btoa(JSON.stringify(rawInstrumentData));
          payload.recurringReference = method.apiReference;
        } catch {
          depositFlowInstance.closeNewTab();
          onError(t('aurumErrors.unknown'));
          return;
        }
      }

      const seonSessionId = await getSeonSessionId();
      if (seonSessionId) {
        payload.seonSessionId = seonSessionId;
      }

      depositFlowInstance.createDepositTransactionMutation.mutate(payload);
    },
    [
      depositFlowInstance,
      paymentsLimitsError,
      paymentInstrumentsError,
      onBeforeTransaction,
      t,
      playerId,
      amount,
      getSeonSessionId,
      getBrowserData,
      getSessionId,
      onError,
      getPaymentMethodKey,
    ]
  );

  return { processDirectDeposit };
}

export { useDirectDepositTransaction };
export type { UseDirectDepositTransactionOptions };
